import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { pageKeyForPath } from "@/lib/permissions";
import Layout from "@/components/Layout";
import PublicBrowse from "@/pages/PublicBrowse";
import LoginPage from "@/pages/Login";
import DashboardPage from "@/pages/Dashboard";
import ManufacturersPage from "@/pages/Manufacturers";
import SeriesPage from "@/pages/Series";
import ProductsPage from "@/pages/Products";
import ProductDetailPage from "@/pages/ProductDetail";
import RollsPage from "@/pages/Rolls";
import RollDetailPage from "@/pages/RollDetail";
import AlertsPage from "@/pages/Alerts";
import CutsPage from "@/pages/Cuts";
import ComingSoon from "@/pages/ComingSoon";
import StockOnHand from "@/pages/StockOnHand";
import IncomingStock from "@/pages/IncomingStock";
import SettingsPage from "@/pages/Settings";
import PerUnitProducts from "@/pages/PerUnitProducts";
import UpdateRequests from "@/pages/UpdateRequests";
import ActivityLog from "@/pages/ActivityLog";
import Reports from "@/pages/Reports";
import ToOrder from "@/pages/ToOrder";
import Archive from "@/pages/Archive";
import Scan from "@/pages/Scan";
import StockTake from "@/pages/StockTake";
import ProductRadar from "@/pages/ProductRadar";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading, isAdmin, canAccessPage, landingPath } = useAuth();
  const location = useLocation();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !isAdmin) return <Navigate to={landingPath} replace />;
  const pageKey = pageKeyForPath(location.pathname);
  if (!canAccessPage(pageKey)) return <Navigate to={landingPath} replace />;
  return children;
}

function RootRedirect() {
  const { user, loading, landingPath } = useAuth();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  }
  return <Navigate to={user ? landingPath : "/login"} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<RootRedirect />} />
          <Route element={<Layout />}>
            <Route path="/browse" element={<ProtectedRoute><PublicBrowse /></ProtectedRoute>} />
            <Route path="/browse/product/:id" element={<ProtectedRoute><ProductDetailPage /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/manufacturers" element={<ProtectedRoute><ManufacturersPage /></ProtectedRoute>} />
            <Route path="/series" element={<ProtectedRoute><SeriesPage /></ProtectedRoute>} />
            <Route path="/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
            <Route path="/rolls" element={<ProtectedRoute><RollsPage /></ProtectedRoute>} />
            <Route path="/rolls/:id" element={<ProtectedRoute><RollDetailPage /></ProtectedRoute>} />
            <Route path="/cuts" element={<ProtectedRoute><CutsPage /></ProtectedRoute>} />
            <Route path="/stock-take" element={<ProtectedRoute><StockTake /></ProtectedRoute>} />
            <Route path="/alerts" element={<ProtectedRoute><AlertsPage /></ProtectedRoute>} />
            <Route path="/stock-on-hand" element={<ProtectedRoute><StockOnHand /></ProtectedRoute>} />
            <Route path="/incoming-stock" element={<ProtectedRoute><IncomingStock /></ProtectedRoute>} />
            <Route path="/to-order" element={<ProtectedRoute><ToOrder /></ProtectedRoute>} />
            <Route path="/archive" element={<ProtectedRoute><Archive /></ProtectedRoute>} />
            <Route path="/per-unit-products" element={<ProtectedRoute><PerUnitProducts /></ProtectedRoute>} />
            <Route path="/scan" element={<ProtectedRoute><Scan /></ProtectedRoute>} />
            <Route path="/update-requests" element={<ProtectedRoute adminOnly><UpdateRequests /></ProtectedRoute>} />
            <Route path="/activity-log" element={<ProtectedRoute adminOnly><ActivityLog /></ProtectedRoute>} />
            <Route path="/product-radar" element={<ProtectedRoute adminOnly><ProductRadar /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
